import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Field,
  Select,
  Input,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  Spinner,
} from '@fluentui/react-components';
import { format, addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCreateRecurringRule } from '../../hooks/useRecurring';
import { useSlots } from '../../hooks/useSlots';
import { BUSINESS_HOURS, DAYS_OF_WEEK, type SlotId } from '@cp-parkingslots/shared';
import { ApiError } from '../../services/api';

const useStyles = makeStyles({
  surface: {
    maxWidth: '450px',
    width: '100%',
    '@media (max-width: 599px)': {
      maxWidth: '100%',
      margin: tokens.spacingHorizontalS,
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  timeRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
  },
  timeField: {
    flex: 1,
  },
  dateRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 599px)': {
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  dateField: {
    flex: 1,
  },
  actions: {
    '@media (max-width: 599px)': {
      flexDirection: 'column-reverse',
      gap: tokens.spacingVerticalS,
      '& button': {
        width: '100%',
      },
    },
  },
});

interface RecurringFormDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function RecurringFormDialog({
  open,
  onClose,
}: RecurringFormDialogProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: slots } = useSlots();
  const createRule = useCreateRecurringRule();

  const today = new Date();
  const defaultValidFrom = format(today, 'yyyy-MM-dd');
  const defaultValidUntil = format(addMonths(today, 3), 'yyyy-MM-dd');

  const [slotId, setSlotId] = useState('SLOT-A');
  const [dayOfWeek, setDayOfWeek] = useState(1); // Monday
  const [startHour, setStartHour] = useState(8);
  const [endHour, setEndHour] = useState(18);
  const [validFrom, setValidFrom] = useState(defaultValidFrom);
  const [validUntil, setValidUntil] = useState(defaultValidUntil);
  const [error, setError] = useState<string | null>(null);

  const hours = [];
  for (let i = BUSINESS_HOURS.startHour; i <= BUSINESS_HOURS.endHour; i++) {
    hours.push(i);
  }

  const resetForm = () => {
    setSlotId('SLOT-A');
    setDayOfWeek(1);
    setStartHour(8);
    setEndHour(18);
    setValidFrom(defaultValidFrom);
    setValidUntil(defaultValidUntil);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (endHour <= startHour) {
      setError(t('reservation.errorEndTime'));
      return;
    }

    if (validUntil < validFrom) {
      setError(t('recurringForm.errorEndDate'));
      return;
    }

    try {
      await createRule.mutateAsync({
        slotId: slotId as SlotId,
        dayOfWeek,
        startHour,
        endHour,
        validFrom,
        validUntil,
      });
      handleClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t('reservation.errorUnexpected'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>{t('recurringForm.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              <Field label={t('recurringForm.dayOfWeek')}>
                <Select
                  value={dayOfWeek.toString()}
                  onChange={(_, data) => setDayOfWeek(parseInt(data.value, 10))}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day.value} value={day.value}>
                      {t(day.labelKey as never)}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label={t('reservation.slot')}>
                <Select
                  value={slotId}
                  onChange={(_, data) => setSlotId(data.value)}
                >
                  {slots?.map((slot) => (
                    <option key={slot.id} value={slot.id}>
                      {t(`slots.${slot.id}` as const)}
                    </option>
                  ))}
                </Select>
              </Field>

              <div className={styles.timeRow}>
                <Field label={t('reservation.from')} className={styles.timeField}>
                  <Select
                    value={startHour.toString()}
                    onChange={(_, data) => setStartHour(parseInt(data.value, 10))}
                  >
                    {hours.slice(0, -1).map((h) => (
                      <option key={h} value={h}>
                        {h.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label={t('reservation.until')} className={styles.timeField}>
                  <Select
                    value={endHour.toString()}
                    onChange={(_, data) => setEndHour(parseInt(data.value, 10))}
                  >
                    {hours.slice(1).map((h) => (
                      <option key={h} value={h}>
                        {h.toString().padStart(2, '0')}:00
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className={styles.dateRow}>
                <Field label={t('recurringForm.validFrom')} className={styles.dateField}>
                  <Input
                    type="date"
                    value={validFrom}
                    onChange={(_, data) => setValidFrom(data.value)}
                  />
                </Field>

                <Field label={t('recurringForm.validUntil')} className={styles.dateField}>
                  <Input
                    type="date"
                    value={validUntil}
                    onChange={(_, data) => setValidUntil(data.value)}
                  />
                </Field>
              </div>

              {error && (
                <MessageBar intent="error">
                  <MessageBarBody>{error}</MessageBarBody>
                </MessageBar>
              )}
            </div>
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button appearance="secondary" onClick={handleClose}>
              {t('reservation.cancel')}
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={createRule.isPending}
            >
              {createRule.isPending ? <Spinner size="tiny" /> : t('recurringForm.create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
